import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import moment from 'moment';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined';
import EventAvailableOutlinedIcon from '@material-ui/icons/EventAvailableOutlined';
import { isDateDue } from '../../helpers/commonHelper';
import { mainTheme } from '../../Theme';
import { TaskDetails } from '../../leadsDetail/LeadsDetailRepository';
import { EnquiryDetail } from '../models/Enquiry';
import { JOURNEY_FOLLOW_UP_CATEGORIES, JOURNEY_EXPANDED_STAGE_LIMIT } from './journeyData';
import { getActivityCategory } from './HistoryActivity';

// Replaces EnquiryJourneyStatsWidget (the old 4-tile Days open/Follow-ups/Overdue/Outcome card) -
// explicit request (reference: headerWidget.png): "the next action is the focal point" - one
// big date/time, the whole card tinted by urgency (blue upcoming, amber due today, red
// overdue), rather than four same-sized numbers competing for attention. Days open moved out
// entirely (now EnquiryFormDrawer.tsx's own header chip, "Open for N days") since it isn't an
// action to take, just a duration - this widget is only about the NEXT thing to do. The
// follow-up count that used to have its own orange tile now lives here as a small badge,
// recoloured to the same blue family as the "upcoming" state (explicit request: orange is
// reserved for urgency, not a plain count).
type FollowUpTone = 'blue' | 'orange' | 'red' | 'green' | 'grey';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 4,
            flex: '0 1 330px',
            // Explicit request: fills its stretched wrapper slot (EnquiryFormDrawer.tsx's
            // journeyNextFollowUpSlot) so this card matches the journey strip's height; content
            // stays vertically centered by justifyContent above.
            height: '100%',
            boxSizing: 'border-box',
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid transparent',
        },
        toneBlue: {
            backgroundColor: 'var(--rv-accent-blue-bg)',
            borderColor: 'rgba(22,103,199,0.25)',
        },
        toneOrange: {
            backgroundColor: 'var(--rv-accent-orange-bg)',
            borderColor: 'rgba(224,90,0,0.25)',
        },
        toneRed: {
            backgroundColor: 'var(--rv-accent-red-bg)',
            borderColor: 'rgba(196,49,75,0.25)',
        },
        toneGreen: {
            backgroundColor: 'var(--rv-accent-green-bg)',
            borderColor: 'rgba(30,123,52,0.25)',
        },
        toneGrey: {
            backgroundColor: 'rgba(0,0,0,0.035)',
            borderColor: 'rgba(0,0,0,0.08)',
        },
        // Explicit bug report: at narrow widths the label ("NEXT FOLLOW-UP") was the one giving
        // up space to the count badge, wrapping mid-word - flexWrap here lets the badge drop to
        // its own line instead once both no longer fit side by side, now that the label itself
        // (flexShrink:0, whiteSpace:nowrap below) never shrinks or wraps.
        eyebrowRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
        },
        eyebrow: {
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            gap: 5,
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            '& svg': {
                fontSize: '14px',
            },
        },
        // Explicit request: the follow-up count moved into this card, recoloured away from the
        // orange the old stats tile used - blue (a plain count, not an urgency signal) rather
        // than a tone that competes with this card's own blue/amber/red state colouring.
        countBadge: {
            flexShrink: 0,
            fontSize: '10.5px',
            fontWeight: 700,
            color: 'var(--rv-accent-blue)',
            backgroundColor: 'rgba(255,255,255,0.65)',
            padding: '2px 7px',
            borderRadius: 999,
            whiteSpace: 'nowrap',
        },
        focal: {
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        subLine: {
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: '12.5px',
            color: mainTheme.TextColor.Secondary,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        },
        subLineText: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        // Explicit request: the assignee's name as a small pill (matching the card's own tone
        // colour rather than a fixed accent) so it stands out from the surrounding sub-line
        // text, instead of reading as one plain run-on string - same semi-transparent-white
        // treatment as countBadge above, so it stays subtle against all 5 tones including red.
        assigneePill: {
            flexShrink: 0,
            fontWeight: 700,
            backgroundColor: 'rgba(255,255,255,0.65)',
            padding: '1px 7px',
            borderRadius: 999,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 160,
        },
    })
);

const toneTextColor: Record<FollowUpTone, string> = {
    blue: 'var(--rv-accent-blue)',
    orange: 'var(--rv-accent-orange)',
    red: 'var(--rv-accent-red)',
    green: 'var(--rv-accent-green)',
    grey: mainTheme.TextColor.Secondary,
};

const toneClassName: Record<FollowUpTone, 'toneBlue' | 'toneOrange' | 'toneRed' | 'toneGreen' | 'toneGrey'> = {
    blue: 'toneBlue',
    orange: 'toneOrange',
    red: 'toneRed',
    green: 'toneGreen',
    grey: 'toneGrey',
};

interface EnquiryNextFollowUpWidgetProps {
    enquiryDetail: EnquiryDetail;
    tasks: TaskDetails[];
}

const EnquiryNextFollowUpWidgetInner: React.FC<EnquiryNextFollowUpWidgetProps> = ({ enquiryDetail, tasks }) => {
    const classes = useStyles();

    const followUpCount = tasks.filter(
        task => JOURNEY_FOLLOW_UP_CATEGORIES.includes(getActivityCategory(task)) && !!task.completedDate
    ).length;

    const isMatterCreated = !!enquiryDetail.matterGuid;
    const isNotProceeding = !isMatterCreated && enquiryDetail.outcome === 'Not Proceeding';
    const isOutcomePending = !isMatterCreated && !isNotProceeding;
    const hasPendingFollowUp = isOutcomePending && !!enquiryDetail.followUpDueDate;

    let tone: FollowUpTone;
    let icon: React.ReactNode;
    let eyebrowLabel: string;
    let focalText: string;
    // Explicit request: the assignee's own name (when this branch shows one at all) rendered as
    // a pill, separate from the plain-text prefix beside it - see assigneePill's own comment.
    let subLinePrefix: string | undefined;
    let assigneeName: string | undefined;

    if (hasPendingFollowUp && enquiryDetail.followUpDueDate) {
        const dueDate = moment(enquiryDetail.followUpDueDate);
        const isOverdue = isDateDue(enquiryDetail.followUpDueDate);
        const isDueToday = !isOverdue && dueDate.isSame(moment(), 'day');

        tone = isOverdue ? 'red' : isDueToday ? 'orange' : 'blue';
        icon = isOverdue ? <ReportProblemOutlinedIcon /> : <AccessTimeIcon />;
        eyebrowLabel = 'Next Follow-up';
        focalText = dueDate.format('ddd D MMM · h:mm a');
        subLinePrefix = isOverdue ? `${dueDate.fromNow(true)} overdue` : isDueToday ? `due ${dueDate.fromNow()}` : dueDate.fromNow();
        assigneeName = enquiryDetail.assignedToName || undefined;
    } else if (isMatterCreated) {
        tone = 'green';
        icon = <WorkOutlineIcon />;
        eyebrowLabel = 'Outcome reached';
        focalText = 'Matter created';
        subLinePrefix = enquiryDetail.matterTitle || undefined;
    } else if (isNotProceeding) {
        tone = 'grey';
        icon = <FlagOutlinedIcon />;
        eyebrowLabel = 'Outcome reached';
        focalText = 'Not proceeding';
        assigneeName = enquiryDetail.assignedToName || undefined;
    } else {
        tone = 'grey';
        icon = <EventAvailableOutlinedIcon />;
        eyebrowLabel = 'Next follow-up';
        focalText = 'Not scheduled';
        if (enquiryDetail.assignedToName) {
            subLinePrefix = 'Assigned to';
            assigneeName = enquiryDetail.assignedToName;
        } else {
            subLinePrefix = 'No follow-up scheduled yet';
        }
    }

    return (
        <div className={clsx(classes.root, classes[toneClassName[tone]])}>
            <div className={classes.eyebrowRow}>
                <span className={classes.eyebrow} style={{ color: toneTextColor[tone] }}>
                    {icon}
                    {eyebrowLabel}
                </span>
                {/* Explicit bug report: once the journey strip collapses (>4 follow-ups), its own
                    collapsed box already shows this same count - this badge is hidden exactly
                    then, so the fact appears once in the header rather than twice. Below that
                    threshold the strip shows individual numbered steps instead (no aggregate
                    count anywhere else), so this badge stays the only place it's shown. */}
                {followUpCount > 0 && followUpCount <= JOURNEY_EXPANDED_STAGE_LIMIT && (
                    <span className={classes.countBadge}>{followUpCount} follow-up{followUpCount === 1 ? '' : 's'}</span>
                )}
            </div>
            <span className={classes.focal} style={{ color: toneTextColor[tone] }}>{focalText}</span>
            {(!!subLinePrefix || !!assigneeName) && (
                <span className={classes.subLine}>
                    {!!subLinePrefix && <span className={classes.subLineText}>{subLinePrefix}</span>}
                    {!!assigneeName && (
                        <span className={classes.assigneePill} style={{ color: toneTextColor[tone] }}>{assigneeName}</span>
                    )}
                </span>
            )}
        </div>
    );
};

// Same rationale/fields as the widget this replaces (EnquiryJourneyStatsWidget's own identical
// comparator) - tasks compared by reference (leadDetailsHistory's own stable array), everything
// else only the specific enquiryDetail fields actually read above.
const areNextFollowUpPropsEqual = (prevProps: EnquiryNextFollowUpWidgetProps, nextProps: EnquiryNextFollowUpWidgetProps): boolean => {
    return prevProps.tasks === nextProps.tasks
        && prevProps.enquiryDetail.matterGuid === nextProps.enquiryDetail.matterGuid
        && prevProps.enquiryDetail.matterTitle === nextProps.enquiryDetail.matterTitle
        && prevProps.enquiryDetail.outcome === nextProps.enquiryDetail.outcome
        && prevProps.enquiryDetail.followUpDueDate === nextProps.enquiryDetail.followUpDueDate
        && prevProps.enquiryDetail.assignedToName === nextProps.enquiryDetail.assignedToName;
};

export const EnquiryNextFollowUpWidget = React.memo(EnquiryNextFollowUpWidgetInner, areNextFollowUpPropsEqual);
